import { useState, useEffect, useRef, useCallback } from 'react';

interface MobileDetectionResult {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  screenSize: {
    width: number;
    height: number;
  };
  orientation: 'portrait' | 'landscape';
  isTouchDevice: boolean;
  userAgent: {
    isIOS: boolean;
    isAndroid: boolean;
    isSafari: boolean;
    isChrome: boolean;
  };
}

export const useMobileDetection = (): MobileDetectionResult => {
  // Use ref to track the latest detection without causing re-renders
  const detectionRef = useRef<MobileDetectionResult | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  
  const [detection, setDetection] = useState<MobileDetectionResult>(() => {
    // Initial detection on mount
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        deviceType: 'desktop',
        screenSize: { width: 1024, height: 768 },
        orientation: 'landscape',
        isTouchDevice: false,
        userAgent: {
          isIOS: false,
          isAndroid: false,
          isSafari: false,
          isChrome: false,
        },
      };
    }

    const initialDetection = getDeviceInfo();
    detectionRef.current = initialDetection;
    return initialDetection;
  });

  function getDeviceInfo(): MobileDetectionResult {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Enhanced mobile detection combining multiple methods
    const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Add hysteresis to prevent rapid switching at breakpoints
    const currentDetection = detectionRef.current;
    let mobileThreshold = 768;
    let tabletThreshold = 1024;
    
    // If we're currently mobile and growing, require extra space before switching to tablet
    if (currentDetection?.isMobile && width > 768) {
      mobileThreshold = 788; // 20px hysteresis
    }
    // If we're currently tablet and shrinking, require smaller size before switching to mobile  
    if (currentDetection?.isTablet && width < 768) {
      mobileThreshold = 748; // 20px hysteresis
    }
    
    // Same for tablet/desktop boundary
    if (currentDetection?.isTablet && width > 1024) {
      tabletThreshold = 1044; // 20px hysteresis
    }
    if (currentDetection?.deviceType === 'desktop' && width < 1024) {
      tabletThreshold = 1004; // 20px hysteresis
    }
    
    const isMobileScreen = width <= mobileThreshold;
    const isTabletScreen = width > mobileThreshold && width <= tabletThreshold;
    
    // Responsive design priority: screen size first, then device detection
    const isMobile = isMobileScreen || isMobileUserAgent;
    const isTablet = !isMobile && (isTabletScreen || /ipad/i.test(userAgent));
    const isDesktop = !isMobile && !isTablet;
    
    // Device type priority: mobile > tablet > desktop
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (isMobile) deviceType = 'mobile';
    else if (isTablet) deviceType = 'tablet';
    
    // Orientation detection
    const orientation: 'portrait' | 'landscape' = height > width ? 'portrait' : 'landscape';
    
    // User agent details (computed once and cached since they don't change)
    const isIOS = /ipad|iphone|ipod/i.test(userAgent);
    const isAndroid = /android/i.test(userAgent);
    const isSafari = /safari/i.test(userAgent) && !/chrome/i.test(userAgent);
    const isChrome = /chrome/i.test(userAgent);

    return {
      isMobile,
      isTablet,
      isDesktop,
      deviceType,
      screenSize: { width, height },
      orientation,
      isTouchDevice,
      userAgent: {
        isIOS,
        isAndroid,
        isSafari,
        isChrome,
      },
    };
  }

  // Check if detection actually changed to prevent unnecessary updates
  function hasDetectionChanged(newDetection: MobileDetectionResult): boolean {
    const current = detectionRef.current;
    if (!current) return true;
    
    // Only trigger updates for significant changes that affect layout
    // Ignore minor size changes that don't change device type
    const significantChange = (
      current.isMobile !== newDetection.isMobile ||
      current.isTablet !== newDetection.isTablet ||
      current.deviceType !== newDetection.deviceType
    );
    
    // For orientation changes, only update if it's a significant size change
    const significantSizeChange = (
      Math.abs(current.screenSize.width - newDetection.screenSize.width) > 50 ||
      Math.abs(current.screenSize.height - newDetection.screenSize.height) > 50
    );
    
    return significantChange || significantSizeChange;
  }

  // Debounced update function to prevent cascading re-renders
  const debouncedUpdate = useCallback(() => {
    // Skip updates during initial app loading to prevent interference
    if (!isInitializedRef.current) {
      return;
    }

    const newDetection = getDeviceInfo();
    
    // Only update if something actually changed
    if (hasDetectionChanged(newDetection)) {
      detectionRef.current = newDetection;
      setDetection(newDetection);
    }
  }, []);

  useEffect(() => {
    // Mark as initialized after first render to prevent interference with app loading
    const initTimer = setTimeout(() => {
      isInitializedRef.current = true;
    }, 100); // Much faster initialization to reduce layout flash

    // Single, debounced resize handler (most reliable across all devices)
    const handleResize = () => {
      // Clear any pending updates
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Schedule debounced update
      debounceTimerRef.current = setTimeout(() => {
        debouncedUpdate();
      }, 50); // Much faster debounce to reduce layout flash during transitions
    };

    // Only use resize listener - it's the most reliable and works on all devices
    // Orientation change events can be unreliable and cause race conditions
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('resize', handleResize);
      
      // Clean up debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [debouncedUpdate]);

  return detection;
}; 